import type { FinancePartnerRow, FinancingProduct, PriceCell } from '../store/oneButtonStore';
import type { PricingToggleProduct } from './financeSelectors';
import { filterPricingProductsForApi } from './financeSelectors';

/** Grid columns — keep in sync with FinancingGridCard FINANCING_PRODUCTS. */
export const PRICING_GRID_PRODUCTS: FinancingProduct[] = [
    'Solar',
    'Roof',
    'Solar + Roof',
    'Battery',
    'Battery + Roof',
    'Solar + Battery',
];

export type PricingDesignFingerprintPayload = Record<string, unknown>;

export type PricingSessionCacheEntry = {
    fingerprint: string;
    /** Product lines requested across merged fetches for this fingerprint. */
    requestedProducts: string[];
    body: unknown;
};

const MAX_SESSION_CACHE_ENTRIES = 2;

/** FIFO session cache: at most two design fingerprints; each entry accumulates merged product slices. */
const pricingSessionCacheQueue: PricingSessionCacheEntry[] = [];

function stableSerialize(value: unknown): string {
    if (value === null || value === undefined) return 'null';
    if (typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`;
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableSerialize(obj[k])}`).join(',')}}`;
}

/** Hash pricing inputs excluding the `products` array (design + utility metrics). */
export function buildPricingDesignFingerprint(payload: PricingDesignFingerprintPayload): string {
    const { products: _products, ...rest } = payload;
    return stableSerialize(rest);
}

function normalizeProductType(productType: string): FinancingProduct | null {
    const t = (productType || '').trim();
    if (!t) return null;
    const lower = t.toLowerCase().replace(/\s+/g, '');
    if (lower === 'solarbatteryroof' || lower === 'solar+battery+roof') return null;
    if (lower === 'solarbattery' || lower === 'solar+battery') return 'Solar + Battery';
    if (lower === 'solarroof' || lower === 'solar+roof') return 'Solar + Roof';
    if (lower === 'batteryroof' || lower === 'battery+roof') return 'Battery + Roof';
    if (lower === 'solar') return 'Solar';
    if (lower === 'roof') return 'Roof';
    if (lower === 'battery') return 'Battery';
    if (PRICING_GRID_PRODUCTS.includes(t as FinancingProduct)) return t as FinancingProduct;
    return null;
}

/** Product grid columns present in a pricing API body. */
export function extractProductLinesFromPricingBody(body: unknown): Set<FinancingProduct> {
    const lines = new Set<FinancingProduct>();
    const raw = ((body as { data?: unknown })?.data ?? body) as Array<{
        pricing?: Array<{ ProductType?: string }>;
    }>;
    if (!Array.isArray(raw)) return lines;
    for (const block of raw) {
        for (const item of block.pricing ?? []) {
            const normalized = normalizeProductType(item.ProductType ?? '');
            if (normalized) lines.add(normalized);
        }
    }
    return lines;
}

/** Map API ProductType names back to opportunity product line names for incremental fetch. */
const GRID_TO_API_PRODUCT: Partial<Record<FinancingProduct, string>> = {
    Solar: 'Solar',
    Roof: 'Roof',
    Battery: 'Battery',
    'Solar + Roof': 'Solar + Roof',
    'Battery + Roof': 'Battery + Roof',
    'Solar + Battery': 'Solar + Battery',
};

export function apiProductLinesForGridColumns(columns: Iterable<FinancingProduct>): string[] {
    const out: string[] = [];
    for (const col of columns) {
        const apiName = GRID_TO_API_PRODUCT[col];
        if (apiName) out.push(apiName);
    }
    return out;
}

export function getPricingSessionCacheForFingerprint(fingerprint: string): PricingSessionCacheEntry | undefined {
    return pricingSessionCacheQueue.find((entry) => entry.fingerprint === fingerprint);
}

export function getCachedProductLinesForFingerprint(fingerprint: string): Set<FinancingProduct> {
    const entry = getPricingSessionCacheForFingerprint(fingerprint);
    if (!entry) return new Set();
    return extractProductLinesFromPricingBody(entry.body);
}

export function clearPricingSessionCache(): void {
    pricingSessionCacheQueue.length = 0;
}

export function mergePricingApiBodies(bodies: unknown[]): unknown {
    const valid = bodies.filter(Boolean);
    if (valid.length === 0) return { data: [] };
    if (valid.length === 1) return valid[0];

    type Block = {
        FinancePartner: string;
        pricing: Array<{ ProductType?: string; [k: string]: unknown }>;
        [k: string]: unknown;
    };

    const blockMap = new Map<string, Block>();

    for (const body of valid) {
        const raw = ((body as { data?: unknown })?.data ?? body) as Block[];
        if (!Array.isArray(raw)) continue;
        for (const block of raw) {
            const partnerKey = String(block.FinancePartner ?? '').toLowerCase();
            let merged = blockMap.get(partnerKey);
            if (!merged) {
                merged = { ...block, pricing: [...(block.pricing ?? [])] };
                blockMap.set(partnerKey, merged);
                continue;
            }
            const existingTypes = new Set(
                (merged.pricing ?? []).map((p) => String(p.ProductType ?? '').toLowerCase()),
            );
            for (const item of block.pricing ?? []) {
                const typeKey = String(item.ProductType ?? '').toLowerCase();
                if (existingTypes.has(typeKey)) continue;
                merged.pricing.push(item);
                existingTypes.add(typeKey);
            }
        }
    }

    return { data: Array.from(blockMap.values()) };
}

export function upsertPricingSessionCache(
    fingerprint: string,
    requestedProducts: string[],
    newBody: unknown,
): void {
    const existing = getPricingSessionCacheForFingerprint(fingerprint);
    if (existing) {
        existing.body = mergePricingApiBodies([existing.body, newBody]);
        existing.requestedProducts = [
            ...new Set([...existing.requestedProducts, ...requestedProducts]),
        ];
        return;
    }

    pricingSessionCacheQueue.push({
        fingerprint,
        requestedProducts: [...requestedProducts],
        body: newBody,
    });
    while (pricingSessionCacheQueue.length > MAX_SESSION_CACHE_ENTRIES) {
        pricingSessionCacheQueue.shift();
    }
}

export function replacePricingSessionCache(
    fingerprint: string,
    requestedProducts: string[],
    body: unknown,
): void {
    const entry: PricingSessionCacheEntry = {
        fingerprint,
        requestedProducts: [...requestedProducts],
        body,
    };
    const idx = pricingSessionCacheQueue.findIndex((e) => e.fingerprint === fingerprint);
    if (idx >= 0) {
        pricingSessionCacheQueue[idx] = entry;
        return;
    }
    pricingSessionCacheQueue.push(entry);
    while (pricingSessionCacheQueue.length > MAX_SESSION_CACHE_ENTRIES) {
        pricingSessionCacheQueue.shift();
    }
}

export function removePricingSessionCacheForFingerprint(fingerprint: string): void {
    const idx = pricingSessionCacheQueue.findIndex((e) => e.fingerprint === fingerprint);
    if (idx >= 0) pricingSessionCacheQueue.splice(idx, 1);
}

/** Lines to request: enabled opportunity products minus columns already cached for this design. */
export function computeIncrementalPricingFetch(
    allOpportunityProducts: string[],
    enabled: Record<PricingToggleProduct, boolean>,
    fingerprint: string,
    forceRefetch: boolean,
): { linesToShow: string[]; linesToFetch: string[]; cachedBody: unknown | null } {
    const linesToShow = filterPricingProductsForApi(allOpportunityProducts, enabled);
    if (linesToShow.length === 0) {
        return { linesToShow, linesToFetch: [], cachedBody: null };
    }

    const cachedEntry = getPricingSessionCacheForFingerprint(fingerprint);
    const cachedBody = cachedEntry?.body ?? null;
    const cachedGridLines = getCachedProductLinesForFingerprint(fingerprint);

    const linesNeededOnGrid = new Set<FinancingProduct>();
    for (const line of linesToShow) {
        const normalized = normalizeProductType(line);
        if (normalized) linesNeededOnGrid.add(normalized);
    }

    if (forceRefetch) {
        return { linesToShow, linesToFetch: linesToShow, cachedBody };
    }

    const missingGridLines = [...linesNeededOnGrid].filter((col) => !cachedGridLines.has(col));
    const linesToFetch = apiProductLinesForGridColumns(missingGridLines);

    return { linesToShow, linesToFetch, cachedBody };
}

function productColumnAllowed(col: FinancingProduct, enabled: Record<PricingToggleProduct, boolean>): boolean {
    const lower = col.toLowerCase();
    if (lower.includes('solar') && !enabled.Solar) return false;
    if (lower.includes('roof') && !enabled.Roof) return false;
    if (lower.includes('battery') && !enabled.Battery) return false;
    return true;
}

/** Hide grid columns for products toggled off (session data retained). */
export function filterFinanceGridByEnabledProducts(
    rows: FinancePartnerRow[],
    enabled: Record<PricingToggleProduct, boolean>,
): FinancePartnerRow[] {
    return rows.map((row) => {
        const prices: Record<string, PriceCell | null> = {};
        for (const col of PRICING_GRID_PRODUCTS) {
            prices[col] = productColumnAllowed(col, enabled) ? (row.prices[col] ?? null) : null;
        }
        return { ...row, prices };
    });
}

/** Whether a toggle product has cached pricing for the current design fingerprint. */
export function isPricingProductCached(
    fingerprint: string | null,
    product: PricingToggleProduct,
): boolean {
    if (!fingerprint) return false;
    const cached = getCachedProductLinesForFingerprint(fingerprint);
    if (product === 'Solar') {
        return [...cached].some((c) => c.toLowerCase().includes('solar'));
    }
    if (product === 'Roof') {
        return [...cached].some((c) => c.toLowerCase().includes('roof'));
    }
    if (product === 'Battery') {
        return [...cached].some((c) => c.toLowerCase().includes('battery'));
    }
    return false;
}
